import { MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Reusable "Chat with X" button. Handles all guardrails:
 *  - not logged in       → sends to /login?next=/chat
 *  - own profile         → hidden (returns null)
 *  - not a paid plan     → toast + redirect to /pricing
 *  - happy path          → creates/opens a conversation and navigates to /chat/:id
 *
 * Props:
 *   userId      – uuid of the other user (required)
 *   label       – button text (default "Chat")
 *   variant     – "primary" | "secondary" | "pill" (default "secondary")
 *   className   – extra Tailwind classes
 *   testid      – override data-testid
 */
export default function ChatButton({ userId, label = "Chat", variant = "secondary", className = "", testid = "chat-cta-btn" }) {
    const { user } = useAuth();
    const nav = useNavigate();

    if (!userId) return null;
    if (user && user.id === userId) return null;

    const styles = {
        primary:   "bg-navy-500 text-white hover:bg-navy-700",
        secondary: "border border-navy-500/20 text-navy-500 hover:bg-navy-500/5",
        pill:      "bg-gold-500 text-navy-900 font-extrabold hover:bg-gold-400 rounded-full",
    }[variant] || "";

    const onClick = async () => {
        if (!user) {
            nav("/login?next=/chat");
            return;
        }
        try {
            const { data } = await api.post("/chat/conversations", { participant_id: userId });
            nav(`/chat/${data.id}`);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) {
                toast.error("Chat is available for subscribers. Redirecting to plans…");
                setTimeout(() => nav("/pricing"), 900);
                return;
            }
            toast.error(err?.response?.data?.detail || "Could not start chat");
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${styles} ${className}`}
        >
            <MessageSquare className="w-4 h-4" />
            {label}
        </button>
    );
}
